import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatDialogRef } from '@angular/material/dialog';
import { GlobalConstants } from '@globalConstants';
import { IFormErrors } from '@interfaces/IFormsErrors';
import { IUserAccessToGroup } from '@interfaces/IGroup';
import { IAppState } from '@myStore/app.state';
import {
  ClearEditStudentAccess,
  EditStudentAccessRequest,
} from '@myStore/groups-changes/groups-changes.actions';
import {
  selectChangesGroupFailure,
  selectChangesGroupLoading,
  selectChangesGroupSuccess,
} from '@myStore/groups-changes/groups-changes.selector';
import {
  ClearGetStudentAccess,
  GetStudentAccessRequest,
} from '@myStore/groups-view/groups-view.actions';
import {
  selectUserAccess,
  selectUserAccessLoading,
} from '@myStore/groups-view/groups-view.selector';
import { IGroupFormErrors } from '@myStore/groups/groups.state';
import { select, Store } from '@ngrx/store';
import { CustomDateAdapter } from '@shared/adapter/date.adapter';
import { FormDialogComponent } from '@shared/components/UI/form-dialog/form-dialog.component';
import { formattedDateWithMinutesSecond } from '@shared/utils/dateUtils';
import { isControlsIsValid } from '@shared/utils/formUtils';
import { MyErrorStateMatcher } from '@validators/matcher';
import {
  CalendarDateFormatter,
  CalendarEventTitleFormatter,
} from 'angular-calendar';
import { parseISO, format } from 'date-fns';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CustomDateFormatter } from '../../../../events/components/calendar/CustomDateFormatter';
import { CustomEventTitleFormatter } from '../../../../events/components/calendar/CutomTitileFormatter';

@Component({
  selector: 'app-student-access-form',
  templateUrl: './student-access-form.component.html',
  styleUrls: ['./student-access-form.component.sass'],
  providers: [
    {
      provide: CalendarDateFormatter,
      useClass: CustomDateFormatter,
    },
    {
      provide: CalendarEventTitleFormatter,
      useClass: CustomEventTitleFormatter,
    },
    {
      provide: DateAdapter,
      useClass: CustomDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: GlobalConstants.DEFAULT_DATEPICKER_FORMAT,
    },
  ],
})
export class StudentAccessFormComponent implements OnInit, OnDestroy {
  private destroy$: Subject<boolean> = new Subject<boolean>();
  public sendLoading$ = this._store.pipe(select(selectChangesGroupLoading));
  public userAccessLoading$ = this._store.pipe(select(selectUserAccessLoading));
  public errors$: IFormErrors | null = null;
  public requiredErrorMessage = 'Поле является обазательным';
  public groupId!: number;
  public studentId!: number;
  public userAccess: IUserAccessToGroup | null = null;
  readonly dialogData = this._dialogRef.componentInstance.data;
  readonly matcher = new MyErrorStateMatcher();
  public studentAccessForm = new UntypedFormGroup({
    start_date: new UntypedFormControl('', Validators.required),
    start_time: new UntypedFormControl('', Validators.required),
    end_date: new UntypedFormControl('', Validators.required),
    end_time: new UntypedFormControl('', Validators.required),
  });

  constructor(
    private _store: Store<IAppState>,
    private _dialogRef: MatDialogRef<FormDialogComponent>
  ) {}

  ngOnInit(): void {
    if (this.dialogData.groupId) {
      this.groupId = this.dialogData.groupId;
    }

    if (this.dialogData.student && this.dialogData.student.id) {
      this.studentId = this.dialogData.student.id;
    }

    this._store.dispatch(
      new GetStudentAccessRequest(this.groupId, this.studentId)
    );

    this._store
      .pipe(select(selectChangesGroupFailure), takeUntil(this.destroy$))
      .subscribe(errors => {
        this.errors$ = errors;
        this.setControlErrors(errors);
      });

    this._store
      .pipe(select(selectChangesGroupSuccess), takeUntil(this.destroy$))
      .subscribe(success => {
        if (success) {
          this.onCancel();
        }
      });

    this._store
      .pipe(select(selectUserAccess), takeUntil(this.destroy$))
      .subscribe(userAccess => {
        if (userAccess) {
          this.userAccess = userAccess;

          this.studentAccessForm.patchValue({
            start_date: format(parseISO(userAccess.start_date), 'yyyy-MM-dd'),
            start_time: this.getStartTime(userAccess.start_date),
            end_date: format(parseISO(userAccess.end_date), 'yyyy-MM-dd'),
            end_time: this.getStartTime(userAccess.end_date),
          });
        }
      });
  }

  combineDateTime(start_date: string, start_time: string): Date {
    const startDate = new Date(start_date);
    const startTime = start_time;
    const [hours, minutes] = startTime.split(':');
    startDate.setHours(Number(hours));
    startDate.setMinutes(Number(minutes));

    return startDate;
  }

  get start_date() {
    return this.studentAccessForm.get('start_date');
  }

  get end_date() {
    return this.studentAccessForm.get('end_date');
  }

  getStartTime(startDate: string): string {
    // Extract time part (hours and minutes) from the start date string
    return startDate.slice(11, 16); // Extracts "00:00"
  }

  onSubmit() {
    const {
      start_date: startDate,
      start_time: startTime,
      end_date: endDate,
      end_time: endTime,
    } = this.studentAccessForm.value;
    const formData = {
      start_date: formattedDateWithMinutesSecond(
        this.combineDateTime(startDate, startTime)
      ),
      end_date: formattedDateWithMinutesSecond(
        this.combineDateTime(endDate, endTime)
      ),
      students: [this.studentId],
    };

    if (isControlsIsValid(this.studentAccessForm.controls)) {
      this._store.dispatch(
        new EditStudentAccessRequest(this.groupId, formData)
      );
    }
  }

  isDisabled(): boolean {
    return !isControlsIsValid(this.studentAccessForm.controls);
  }

  onCancel() {
    this._store.dispatch(new ClearEditStudentAccess());
    this._store.dispatch(new ClearGetStudentAccess());
    this._dialogRef.close();
  }

  setControlErrors(errors: IGroupFormErrors | null): void {
    if (errors) {
      Object.keys(errors).forEach(key => {
        if (this.studentAccessForm.controls[key]) {
          this.studentAccessForm.controls[key].setErrors({
            errors: errors[key],
          });
        }
      });
    }
  }

  getMessageFormError(name: string): string | null {
    if (this.errors$ && this.errors$[name]) {
      return this.errors$[name][0];
    }

    return null;
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
